import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Star, Users, Copy, Check, ChevronRight, UserPlus } from 'lucide-react';
import { post } from '@/shared/api/api_client';
import { useAuth } from '@/features/auth';
import { Button, Input, Card } from '@/shared/ui';
import { cn } from '@/shared/utils/cn';
import type { Family } from '@/entities/family';

// ---------------------------------------------------------------------------
// Validation schemas
// ---------------------------------------------------------------------------
const familySchema = z.object({
  name: z.string().min(2, 'Family name must be at least 2 characters').max(50, 'Max 50 characters'),
});

const childSchema = z.object({
  displayName: z.string().min(2, 'Name must be at least 2 characters').max(40),
  username: z
    .string()
    .min(3, 'Username must be at least 3 characters')
    .max(20, 'Max 20 characters')
    .regex(/^[a-z0-9_]+$/, 'Only lowercase letters, numbers, underscores'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

type FamilyFormValues = z.infer<typeof familySchema>;
type ChildFormValues = z.infer<typeof childSchema>;

// ---------------------------------------------------------------------------
// Step indicator
// ---------------------------------------------------------------------------
function StepIndicator({ current, total }: { current: number; total: number }): React.ReactElement {
  return (
    <div className="flex items-center gap-2" aria-label={`Step ${current} of ${total}`}>
      {Array.from({ length: total }, (_, i) => (
        <React.Fragment key={i}>
          <div
            className={cn(
              'flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition-colors duration-300',
              i + 1 === current
                ? 'bg-primary-600 text-white'
                : i + 1 < current
                ? 'bg-primary-200 text-primary-700'
                : 'bg-slate-100 text-slate-400',
            )}
            aria-current={i + 1 === current ? 'step' : undefined}
          >
            {i + 1 < current ? <Check size={14} /> : i + 1}
          </div>
          {i < total - 1 && (
            <div
              className={cn(
                'h-0.5 flex-1 rounded transition-colors duration-300',
                i + 1 < current ? 'bg-primary-300' : 'bg-slate-100',
              )}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Invite link copy button
// ---------------------------------------------------------------------------
function CopyLinkButton({ link }: { link: string }): React.ReactElement {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    void navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs text-slate-500">Share this invite link with a co-parent:</p>
      <div className="flex gap-2">
        <div className="flex-1 truncate rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
          {link}
        </div>
        <button
          type="button"
          onClick={handleCopy}
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors duration-150',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
            copied
              ? 'bg-secondary-100 text-secondary-700'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200',
          )}
          aria-label={copied ? 'Copied!' : 'Copy invite link'}
        >
          {copied ? <Check size={16} /> : <Copy size={16} />}
        </button>
      </div>
      {copied && (
        <p className="text-xs text-secondary-600 font-medium" role="status">
          Link copied to clipboard!
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// OnboardingPage
// ---------------------------------------------------------------------------
export default function OnboardingPage(): React.ReactElement {
  const navigate = useNavigate();
  const { appUser, setAppUser } = useAuth();

  const [step, setStep] = useState(1);
  const [createdFamily, setCreatedFamily] = useState<Family | null>(null);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [createInviteLoading, setCreateInviteLoading] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  // Step 1: Create family form
  const {
    register: registerFamily,
    handleSubmit: handleFamilySubmit,
    formState: { errors: familyErrors, isSubmitting: familySubmitting },
    setError: setFamilyError,
  } = useForm<FamilyFormValues>({ resolver: zodResolver(familySchema) });

  // Step 3: Add child form
  const {
    register: registerChild,
    handleSubmit: handleChildSubmit,
    formState: { errors: childErrors, isSubmitting: childSubmitting },
    setError: setChildError,
  } = useForm<ChildFormValues>({ resolver: zodResolver(childSchema) });

  // Step 1: Create family
  const onFamilySubmit = async (data: FamilyFormValues) => {
    setServerError(null);
    try {
      const family = await post<Family>('/families', { name: data.name });
      setCreatedFamily(family);
      // Update app user in context (they now have a familyId)
      if (appUser) {
        setAppUser({ ...appUser, familyId: family.id });
      }
      setStep(2);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create family.';
      setFamilyError('root', { message });
    }
  };

  // Step 2: Generate invite link
  const handleGenerateInvite = async () => {
    if (!createdFamily) return;
    setCreateInviteLoading(true);
    setServerError(null);
    try {
      const res = await post<{ shareableLink: string }>(`/families/${createdFamily.id}/invitations`);
      setInviteLink(res.shareableLink);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to generate invite link.';
      setServerError(message);
    } finally {
      setCreateInviteLoading(false);
    }
  };

  // Step 3: Add child
  const onChildSubmit = async (data: ChildFormValues) => {
    if (!createdFamily) return;
    setServerError(null);
    try {
      await post<void>(`/families/${createdFamily.id}/children`, data);
      navigate('/dashboard');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to add child.';
      setChildError('root', { message });
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-4 py-12">
      {/* Logo */}
      <div className="mb-8 flex items-center gap-2 text-primary-700">
        <Star size={28} className="text-accent-500 fill-accent-400" aria-hidden="true" />
        <span className="text-2xl font-extrabold tracking-tight">KidKred</span>
      </div>

      <Card className="w-full max-w-md" shadow="md">
        {/* Step indicator */}
        <div className="mb-6">
          <StepIndicator current={step} total={3} />
        </div>

        {/* ---- Step 1: Create family ---- */}
        {step === 1 && (
          <div className="flex flex-col gap-5">
            <div className="text-center">
              <div className="mb-3 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-100 text-primary-600">
                <Users size={28} aria-hidden="true" />
              </div>
              <h1 className="text-xl font-bold text-slate-900">Create your family</h1>
              <p className="mt-1 text-sm text-slate-500">Give your family a name to get started.</p>
            </div>

            <form onSubmit={handleFamilySubmit(onFamilySubmit)} noValidate className="flex flex-col gap-4">
              <Input
                label="Family name"
                placeholder="e.g. The Smiths"
                autoFocus
                error={familyErrors.name?.message}
                {...registerFamily('name')}
              />
              {familyErrors.root && (
                <p role="alert" className="text-xs text-danger-600">{familyErrors.root.message}</p>
              )}
              <Button
                type="submit"
                isLoading={familySubmitting}
                fullWidth
                size="lg"
                rightIcon={<ChevronRight size={18} aria-hidden="true" />}
              >
                Create Family
              </Button>
            </form>
          </div>
        )}

        {/* ---- Step 2: Family created + invite ---- */}
        {step === 2 && createdFamily && (
          <div className="flex flex-col gap-6">
            <div className="text-center">
              <div className="mb-3 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-secondary-100 text-secondary-600 text-3xl">
                🎉
              </div>
              <h1 className="text-xl font-bold text-slate-900">
                {createdFamily.name} is ready!
              </h1>
              <p className="mt-1 text-sm text-slate-500">
                Your family has been created. Next, invite a co-parent (optional).
              </p>
            </div>

            {inviteLink ? (
              <CopyLinkButton link={inviteLink} />
            ) : (
              <Button
                variant="secondary"
                fullWidth
                isLoading={createInviteLoading}
                onClick={() => void handleGenerateInvite()}
                leftIcon={<UserPlus size={16} />}
              >
                Invite a co-parent
              </Button>
            )}

            {serverError && (
              <p role="alert" className="text-xs text-danger-600">{serverError}</p>
            )}

            <Button
              fullWidth
              size="lg"
              rightIcon={<ChevronRight size={18} aria-hidden="true" />}
              onClick={() => setStep(3)}
            >
              Next: Add a child
            </Button>
          </div>
        )}

        {/* ---- Step 3: Add first child ---- */}
        {step === 3 && (
          <div className="flex flex-col gap-5">
            <div className="text-center">
              <div className="mb-3 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-accent-100 text-accent-600 text-3xl">
                👦
              </div>
              <h1 className="text-xl font-bold text-slate-900">Add your first child</h1>
              <p className="mt-1 text-sm text-slate-500">
                Create a login for your child. They'll use it to view chores and rewards.
              </p>
            </div>

            <form onSubmit={handleChildSubmit(onChildSubmit)} noValidate className="flex flex-col gap-4">
              <Input
                label="Child's name"
                placeholder="e.g. Alex"
                autoFocus
                error={childErrors.displayName?.message}
                {...registerChild('displayName')}
              />
              <Input
                label="Username"
                placeholder="e.g. alex123"
                hint="Lowercase letters, numbers, underscores only"
                error={childErrors.username?.message}
                {...registerChild('username')}
              />
              <Input
                label="Password"
                type="password"
                placeholder="Create a simple password"
                error={childErrors.password?.message}
                {...registerChild('password')}
              />
              {childErrors.root && (
                <p role="alert" className="text-xs text-danger-600">{childErrors.root.message}</p>
              )}
              {serverError && (
                <p role="alert" className="text-xs text-danger-600">{serverError}</p>
              )}
              <Button
                type="submit"
                isLoading={childSubmitting}
                fullWidth
                size="lg"
              >
                Add Child &amp; Finish
              </Button>
            </form>

            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className={cn(
                'text-center text-sm text-slate-500 hover:text-slate-700',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded',
              )}
            >
              I'll do this later →
            </button>
          </div>
        )}
      </Card>
    </div>
  );
}
